"use client";

import {
  AnimatePresence,
  motion,
  useMotionTemplate,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Waypoints } from "lucide-react";
import { cn } from "@routecore/core";

/* ------------------------------- 3D tilt card ------------------------------ */

/**
 * Wraps content in a card that tilts toward the cursor in 3D, lifts on hover
 * and catches a soft specular glare. Pointer-driven values are spring-damped
 * so the motion settles instead of snapping.
 */
export function Tilt3D({
  children,
  className,
  intensity = 9,
  lift = -6,
  scale = 1.02,
  glare = true,
  radius = "1rem",
}: {
  children: ReactNode;
  className?: string;
  intensity?: number;
  lift?: number;
  scale?: number;
  glare?: boolean;
  radius?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [hovering, setHovering] = useState(false);

  const px = useMotionValue(0);
  const py = useMotionValue(0);

  const spring = { stiffness: 260, damping: 26, mass: 0.6 };
  const sx = useSpring(px, spring);
  const sy = useSpring(py, spring);

  const rotateY = useTransform(sx, (v) => v * intensity);
  const rotateX = useTransform(sy, (v) => -v * intensity);

  const glareX = useTransform(sx, (v) => `${50 + v * 110}%`);
  const glareY = useTransform(sy, (v) => `${50 + v * 110}%`);
  const glareBg = useMotionTemplate`radial-gradient(420px circle at ${glareX} ${glareY}, rgba(255,255,255,0.55), transparent 45%)`;

  const handleMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width - 0.5);
    py.set((e.clientY - r.top) / r.height - 0.5);
  };

  const reset = () => {
    px.set(0);
    py.set(0);
    setHovering(false);
  };

  return (
    <motion.div
      ref={ref}
      onPointerMove={handleMove}
      onPointerEnter={() => setHovering(true)}
      onPointerLeave={reset}
      animate={{ y: hovering ? lift : 0, scale: hovering ? scale : 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 24 }}
      style={{
        rotateX,
        rotateY,
        transformPerspective: 1100,
        transformStyle: "preserve-3d",
        borderRadius: radius,
      }}
      className={cn("relative will-change-transform", className)}
    >
      {children}

      {/* specular glare */}
      {glare && (
        <motion.span
          aria-hidden
          className="pointer-events-none absolute inset-0 z-20 opacity-0 mix-blend-soft-light transition-opacity duration-300"
          style={{
            background: glareBg,
            borderRadius: radius,
            opacity: hovering ? 0.9 : 0,
          }}
        />
      )}

      {/* contact shadow that deepens on hover */}
      <motion.span
        aria-hidden
        className="pointer-events-none absolute -inset-px -z-10 blur-xl"
        animate={{ opacity: hovering ? 0.55 : 0 }}
        transition={{ duration: 0.35 }}
        style={{
          borderRadius: radius,
          background: "rgba(0,0,0,0.7)",
        }}
      />
    </motion.div>
  );
}

/* ----------------------------- rolling numbers ----------------------------- */

const REEL_CYCLES = 4;
const LINE = 1.15; // em per digit row

function DigitReel({ digit, delay }: { digit: number; delay: number }) {
  const strip: number[] = [];
  for (let c = 0; c < REEL_CYCLES; c++) for (let d = 0; d < 10; d++) strip.push(d);
  const target = (REEL_CYCLES - 1) * 10 + digit;

  return (
    <span
      className="relative inline-block overflow-hidden align-baseline tabular-nums"
      style={{ height: `${LINE}em`, lineHeight: `${LINE}em` }}
    >
      {/* invisible sizer keeps column width stable */}
      <span className="invisible block">0</span>
      <motion.span
        className="absolute inset-x-0 top-0 block"
        initial={{ y: 0 }}
        animate={{ y: `-${target * LINE}em` }}
        transition={{
          type: "spring",
          stiffness: 46,
          damping: 15,
          mass: 1.05,
          delay,
          restDelta: 0.0008,
        }}
      >
        {strip.map((d, i) => (
          <span key={i} className="block" style={{ height: `${LINE}em`, lineHeight: `${LINE}em` }}>
            {d}
          </span>
        ))}
      </motion.span>
    </span>
  );
}

/**
 * Slot-machine counter: every digit spins through several cycles and eases to
 * a stop on its final value, cascading left → right.
 */
export function RollingNumber({
  value,
  className,
  delay = 0,
  animate = true,
}: {
  value: string | number;
  className?: string;
  delay?: number;
  animate?: boolean;
}) {
  const text = String(value);

  if (!animate) {
    return <span className={cn("tabular-nums", className)}>{text}</span>;
  }

  let digitIndex = 0;
  return (
    <span
      className={cn("inline-flex items-baseline tabular-nums", className)}
      style={{ verticalAlign: "baseline" }}
      aria-label={text}
    >
      {text.split("").map((ch, i) => {
        if (ch >= "0" && ch <= "9") {
          const d = digitIndex++;
          return <DigitReel key={`${i}-${ch}`} digit={Number(ch)} delay={delay + d * 0.09} />;
        }
        return (
          <span key={`${i}-${ch}`} aria-hidden>
            {ch}
          </span>
        );
      })}
    </span>
  );
}

/* --------------------------------- reveal ---------------------------------- */

/** Staggered entrance for page sections. */
export function Reveal({
  children,
  delay = 0,
  y = 18,
  className,
}: {
  children: ReactNode;
  delay?: number;
  y?: number;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y, filter: "blur(6px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ duration: 0.62, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------ intro curtain ------------------------------ */

/** Full-screen brand curtain shown while the studio boots on first load. */
export function IntroCurtain({ show }: { show: boolean }) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="curtain"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.06, filter: "blur(10px)" }}
          transition={{ duration: 0.75, ease: [0.65, 0, 0.35, 1] }}
          className="map-dots fixed inset-0 z-[120] grid place-items-center bg-paper"
        >
          <div className="flex flex-col items-center">
            {/* mark */}
            <motion.span
              initial={{ opacity: 0, scale: 0.6, rotate: -22 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 150, damping: 13, delay: 0.05 }}
              className="relative grid size-20 place-items-center rounded-[28px] bg-porcelain shadow-lifted p-2.5 border border-line"
            >
              <motion.span
                className="absolute inset-0 rounded-[28px] border-2 border-accent"
                initial={{ opacity: 0.9, scale: 1 }}
                animate={{ opacity: 0, scale: 1.55 }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeOut" }}
              />
              <img src="/iSource LOGO1.png" alt="iSource" className="w-full h-full object-contain" />
            </motion.span>

            {/* wordmark */}
            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 font-display text-3xl font-semibold tracking-tight text-ink"
            >
              iSource RouteCore
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.42, ease: [0.22, 1, 0.36, 1] }}
              className="mt-2 text-[10px] font-bold tracking-[0.34em] text-ink-faint uppercase"
            >
              Team Itinerary System
            </motion.p>

            {/* route progress line */}
            <div className="relative mt-8 h-px w-56 overflow-hidden bg-line">
              <motion.span
                className="absolute inset-y-0 left-0 bg-accent"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 1.15, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
              />
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-3 font-display text-xs text-ink-faint italic"
            >
              Charting the routes…
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
